import { Trans, useLingui } from '@lingui/react/macro';
import type { RotaProposal } from '@repo/contracts';
import { useMutation } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { commitGeneration, createGeneration } from '@/api/rotagen-api';
import { PageHeader } from '@/components/blocks/page-header';
import { PostCommitFeedback } from '@/components/feedback/post-commit-feedback';
import { QueryError } from '@/components/query-error';
import { ApplyToRotaModal } from '@/components/templates/apply-to-rota-modal';
import type { SingleModeSubmitData } from '@/components/templates/apply-to-rota-modal';
import { DuplicateTemplateModal } from '@/components/templates/duplicate-template-modal';
import { ProposalView } from '@/components/build-workspace/proposal-view';
import { TemplatePreviewModal } from '@/components/build-workspace/template-preview-modal';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { TemplatesBanner } from '@/components/templates/templates-banner';
import { TemplatesBulkActions } from '@/components/templates/templates-bulk-actions';
import { TemplatesEmptyState } from '@/components/templates/templates-empty-state';
import { TemplatesFilters } from '@/components/templates/templates-filters';
import { TemplatesHeader } from '@/components/templates/templates-header';
import { TemplatesSkeleton } from '@/components/templates/templates-skeleton';
import { TemplatesTable } from '@/components/templates/templates-table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { applyFiltersAndSort } from '@/domain/templates/apply-filters-and-sort';
import { exportTemplateToExcel } from '@/domain/templates/excel-import';
import { mapSavedTemplateToRotaTemplate } from '@/domain/rotagen/rotagen-mapper';
import { forecastForWeek } from '@/domain/templates/demand-scaling';
import type { DemandForecast } from '@/domain/templates/demand-scaling';
import { useLandingFilters } from '@/hooks/use-landing-filters';
import { useShiftTypes } from '@/hooks/use-shift-types';
import { useSites } from '@/hooks/use-sites';
import {
  useDeleteTemplate,
  useDuplicateTemplate,
  usePublishTemplate,
  useRestoreTemplate,
  useTemplates,
  useUnpublishTemplate,
} from '@/hooks/use-templates';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { addOneDayISO, enumerateWeekDays } from '@/lib/date-utils';
import type { SavedTemplate, TemplateStatus } from '@/types/saved-template';
import type { Site } from '@/types/template';

/**
 * Templates landing page — `/` route (Task 6.17).
 *
 * Integrating consumer of every PR #2–#6 component: the URL-driven
 * filter state, the table + filters + bulk actions, the four landing
 * modals (Preview / Duplicate / Apply-to-Rota → Proposal-Preview),
 * and the mutation hooks. Owns the modal-open state, the bulk-selection
 * `Set`, and the two-step Apply-to-Rota flow (TM1 form →
 * `createGeneration` → TM4 proposal → `commitGeneration`).
 *
 * Toast wiring uses the success/error callbacks on each mutation call
 * rather than the hook-level `onSuccess` so the toast copy can reference
 * the specific template the user acted on.
 */
export function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useLingui();

  // Briefly highlight a template just activated in the workspace (passed via
  // navigation state). Read once into state, then flash for a few seconds: clear
  // the router state (so a refresh / back doesn't re-flag it) and the highlight.
  const [justActivatedId, setJustActivatedId] = useState<string | undefined>(
    () => (location.state as { activatedId?: string } | null)?.activatedId,
  );
  useEffect(() => {
    if (!justActivatedId) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    const timer = setTimeout(() => setJustActivatedId(undefined), 5000);
    return () => clearTimeout(timer);
    // Run once when an activated id arrives; `navigate`/`location` are stable enough here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justActivatedId]);

  const { filters, setFilter, clearAll, hasActiveFilters } = useLandingFilters();
  const templatesQuery = useTemplates(filters.statuses);
  const sitesQuery = useSites();
  const shiftTypesQuery = useShiftTypes();

  const deleteMutation = useDeleteTemplate();
  const duplicateMutation = useDuplicateTemplate();
  const restoreMutation = useRestoreTemplate();
  const publishMutation = usePublishTemplate();
  const unpublishMutation = useUnpublishTemplate();

  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<OpenModal>(null);

  // The generation whose rota was most recently committed. Drives the
  // one-shot post-commit feedback prompt (F3 / D28); `null` until the
  // user successfully commits a proposal this session.
  const [committedGenerationId, setCommittedGenerationId] = useState<string | null>(null);

  // Pending delete awaiting confirmation. Delete moves templates to Deleted
  // (restorable), so a single or bulk delete first opens a confirmation
  // dialog rather than mutating immediately.
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);

  const sites = useMemo<Site[]>(() => sitesQuery.data ?? [], [sitesQuery.data]);
  const shiftTypes = useMemo(() => shiftTypesQuery.data ?? [], [shiftTypesQuery.data]);

  // The persisted scope already carries site names (contract `Identifiable[]`),
  // so the table renders them directly — just apply the URL-driven filter + sort.
  const filteredTemplates = useMemo(() => {
    if (!templatesQuery.data) return [];
    return applyFiltersAndSort(templatesQuery.data, filters);
  }, [templatesQuery.data, filters]);

  // The selection restricted to rows in the currently fetched (server-filtered)
  // list. Every count, gate and bulk handler reads this, so a row that has left
  // the list (filter change, delete, restore) can never be acted on.
  const visibleSelection = useMemo<string[]>(() => {
    if (!templatesQuery.data) return [];
    const visibleIds = new Set(templatesQuery.data.map((tpl) => tpl.id));
    return Array.from(selection).filter((id) => visibleIds.has(id));
  }, [selection, templatesQuery.data]);

  // Distinct statuses across the visible selection — drives which lifecycle
  // actions the bulk bar offers.
  const selectedStatuses = useMemo<TemplateStatus[]>(() => {
    if (!templatesQuery.data) return [];
    const statusById = new Map(templatesQuery.data.map((tpl) => [tpl.id, tpl.status]));
    const present = new Set<TemplateStatus>();
    for (const id of visibleSelection) {
      const status = statusById.get(id);
      if (status) present.add(status);
    }
    return Array.from(present);
  }, [visibleSelection, templatesQuery.data]);

  // A different status filter is a different list, so start with a clean selection.
  const statusFilterKey = filters.statuses.join(',');
  useEffect(() => {
    setSelection(new Set());
  }, [statusFilterKey]);

  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(filteredTemplates.length / filters.pageSize);
  const pagedTemplates = filteredTemplates.slice(
    (page - 1) * filters.pageSize,
    page * filters.pageSize,
  );

  // Reset to page 1 whenever the filter changes (including page size) so the
  // user never ends up on a page that no longer has results.
  useEffect(() => {
    setPage(1);
  }, [filters]);

  // The whole generation — date conversion, forecast build, template→contract
  // mapping, and the API call — runs inside the mutation so every failure mode
  // (notably the synchronous `RotaTemplateSchema` validation throw from
  // `mapSavedTemplateToRotaTemplate`) drives the mutation's `isPending`
  // (loading) and `error` (surfaced in the modal) rather than being swallowed.
  const generateMutation = useMutation({
    mutationFn: async (vars: {
      template: SavedTemplate;
      site: Site;
      dateRange: string;
      timeZone: string;
      demand?: DemandForecast;
    }) => {
      // The form's dateRange is `weekStart/weekEnd` with end inclusive; rotagen's
      // API uses a half-open interval (end exclusive), so bump end by one day.
      const [start, endInclusive] = vars.dateRange.split('/');
      const exclusiveEnd = addOneDayISO(endInclusive);
      if (!start || !exclusiveEnd) {
        throw new Error(
          'Could not determine the rota week. Please reselect the week and try again.',
        );
      }

      // Per-day forecast (D56) from the modal's what-if + the template's scaling
      // baseline, keyed by the week's inclusive ISO dates. `forecastForWeek`
      // returns undefined when scaling is off, so non-scaling generations send none.
      const forecast = forecastForWeek(
        vars.template.demandScaling,
        vars.demand ?? {},
        enumerateWeekDays(start, endInclusive),
      );

      return createGeneration({
        site: { id: vars.site.id, name: vars.site.name },
        template: mapSavedTemplateToRotaTemplate(vars.template, shiftTypes),
        dateRange: `${start}/${exclusiveEnd}`,
        timeZone: vars.timeZone,
        ...(forecast ? { forecast } : {}),
      });
    },
  });
  const commitMutation = useMutation({
    mutationFn: (vars: { generationId: string; replaceExistingRota: boolean }) =>
      commitGeneration(vars.generationId, { replaceExistingRota: vars.replaceExistingRota }),
  });

  /** Closes whichever modal is open and resets any in-flight mutation error state. */
  const closeModal = () => {
    setModal(null);
    generateMutation.reset();
    commitMutation.reset();
  };

  /** Open the wizard for a brand-new template. */
  const handleCreateNew = () => navigate('/templates/new');

  /** Open the wizard for editing an existing template. */
  const handleEdit = (template: SavedTemplate) => navigate(`/templates/${template.id}`);

  /** Row-action Delete: opens the delete confirmation. */
  const handleDelete = (template: SavedTemplate) => {
    setPendingDelete({ kind: 'single', template });
  };

  /** Runs the actual single delete once confirmed (optimistic under the hood). */
  const runSingleDelete = async (template: SavedTemplate) => {
    try {
      await deleteMutation.mutateAsync(template.id);
      toast({
        title: t`Template deleted`,
        description: t`"${template.name}" was deleted.`,
      });
    } catch (err) {
      toast({
        title: t`Delete failed`,
        description: err instanceof Error ? err.message : t`Unknown error`,
        variant: 'destructive',
      });
    }
  };

  /** Restore a deleted template to the draft or published state it had before deletion. */
  const handleRestore = async (template: SavedTemplate) => {
    try {
      await restoreMutation.mutateAsync(template.id);
      toast({
        title: t`Template restored`,
        description: t`"${template.name}" was restored.`,
      });
    } catch (err) {
      toast({
        title: t`Restore failed`,
        description: err instanceof Error ? err.message : t`Unknown error`,
        variant: 'destructive',
      });
    }
  };

  /** Publish a draft template. */
  const handlePublish = async (template: SavedTemplate) => {
    try {
      await publishMutation.mutateAsync(template.id);
      toast({
        title: t`Template published`,
        description: t`"${template.name}" is now published.`,
      });
    } catch (err) {
      toast({
        title: t`Publish failed`,
        description: err instanceof Error ? err.message : t`Unknown error`,
        variant: 'destructive',
      });
    }
  };

  /** Unpublish a published template. */
  const handleUnpublish = async (template: SavedTemplate) => {
    try {
      await unpublishMutation.mutateAsync(template.id);
      toast({
        title: t`Template unpublished`,
        description: t`"${template.name}" is now a draft.`,
      });
    } catch (err) {
      toast({
        title: t`Unpublish failed`,
        description: err instanceof Error ? err.message : t`Unknown error`,
        variant: 'destructive',
      });
    }
  };

  /** Export a single template's shifts to Excel. */
  const handleExport = (template: SavedTemplate) => {
    exportTemplateToExcel(template.name, template.shifts, shiftTypes);
  };

  /** Bulk-action Delete: opens the delete confirmation. */
  const handleBulkDelete = () => {
    if (visibleSelection.length === 0) return;
    setPendingDelete({ kind: 'bulk', ids: visibleSelection });
  };

  /**
   * Runs the actual bulk delete once confirmed. Each call is optimistic so
   * the rows disappear immediately; errors surface as a single aggregated
   * toast at the end.
   */
  const runBulkDelete = async (ids: string[]) => {
    let failures = 0;
    for (const id of ids) {
      try {
        await deleteMutation.mutateAsync(id);
      } catch {
        failures += 1;
      }
    }
    setSelection(new Set());
    if (failures > 0) {
      toast({
        title: t`Bulk delete partially failed`,
        description: t`${ids.length - failures} of ${ids.length} template(s) deleted.`,
        variant: 'destructive',
      });
    } else {
      toast({
        title: t`Bulk delete complete`,
        description: t`${ids.length} template(s) deleted.`,
      });
    }
  };

  /** Confirms the pending (single or bulk) delete and runs it. */
  const confirmDelete = async () => {
    const pending = pendingDelete;
    setPendingDelete(null);
    if (!pending) return;
    if (pending.kind === 'single') {
      await runSingleDelete(pending.template);
    } else {
      await runBulkDelete(pending.ids);
    }
  };

  /**
   * Bulk-duplicate every selected template. Each call is independent;
   * errors are caught per-iteration and aggregated into a single
   * toast at the end (mirrors `handleBulkDelete`).
   */
  const handleBulkDuplicate = async () => {
    const ids = visibleSelection;
    let failures = 0;
    for (const id of ids) {
      try {
        await duplicateMutation.mutateAsync({ id });
      } catch {
        failures += 1;
      }
    }
    setSelection(new Set());
    if (failures > 0) {
      toast({
        title: t`Bulk duplicate partially failed`,
        description: t`${ids.length - failures} of ${ids.length} template(s) duplicated.`,
        variant: 'destructive',
      });
    } else {
      toast({
        title: t`Bulk duplicate complete`,
        description: t`${ids.length} template(s) duplicated.`,
      });
    }
  };

  /** Bulk-export every selected template — fires one Excel download per row. */
  const handleBulkExport = () => {
    if (!templatesQuery.data) return;
    const ids = visibleSelection;
    for (const id of ids) {
      const tpl = templatesQuery.data.find((x) => x.id === id);
      if (tpl) exportTemplateToExcel(tpl.name, tpl.shifts, shiftTypes);
    }
  };

  /**
   * Bulk-publish every selected draft. Only offered for a homogeneous draft
   * selection (the bulk bar gates the button), so every id is eligible.
   * Failures aggregate into one toast (mirrors `handleBulkDuplicate`).
   */
  const handleBulkPublish = async () => {
    const ids = visibleSelection;
    let failures = 0;
    for (const id of ids) {
      try {
        await publishMutation.mutateAsync(id);
      } catch {
        failures += 1;
      }
    }
    setSelection(new Set());
    if (failures > 0) {
      toast({
        title: t`Bulk publish partially failed`,
        description: t`${ids.length - failures} of ${ids.length} template(s) published.`,
        variant: 'destructive',
      });
    } else {
      toast({
        title: t`Bulk publish complete`,
        description: t`${ids.length} template(s) published.`,
      });
    }
  };

  /** Bulk-unpublish every selected published template (homogeneous selection). */
  const handleBulkUnpublish = async () => {
    const ids = visibleSelection;
    let failures = 0;
    for (const id of ids) {
      try {
        await unpublishMutation.mutateAsync(id);
      } catch {
        failures += 1;
      }
    }
    setSelection(new Set());
    if (failures > 0) {
      toast({
        title: t`Bulk unpublish partially failed`,
        description: t`${ids.length - failures} of ${ids.length} template(s) unpublished.`,
        variant: 'destructive',
      });
    } else {
      toast({
        title: t`Bulk unpublish complete`,
        description: t`${ids.length} template(s) unpublished.`,
      });
    }
  };

  /** Bulk-restore every selected deleted template (homogeneous selection). */
  const handleBulkRestore = async () => {
    const ids = visibleSelection;
    let failures = 0;
    for (const id of ids) {
      try {
        await restoreMutation.mutateAsync(id);
      } catch {
        failures += 1;
      }
    }
    setSelection(new Set());
    if (failures > 0) {
      toast({
        title: t`Bulk restore partially failed`,
        description: t`${ids.length - failures} of ${ids.length} template(s) restored.`,
        variant: 'destructive',
      });
    } else {
      toast({
        title: t`Bulk restore complete`,
        description: t`${ids.length} template(s) restored.`,
      });
    }
  };

  /** TM2: Confirm a rename-only duplicate. Closes the modal on success. */
  const handleDuplicateConfirm = async (args: { id: string; newName: string }) => {
    try {
      await duplicateMutation.mutateAsync(args);
      toast({
        title: t`Template duplicated`,
        description: t`Created "${args.newName}".`,
      });
      closeModal();
    } catch (err) {
      toast({
        title: t`Duplicate failed`,
        description: err instanceof Error ? err.message : t`Unknown error`,
        variant: 'destructive',
      });
    }
  };

  /**
   * TM1 submit: maps the SavedTemplate to the contract `RotaTemplate`,
   * looks up the selected site, calls `createGeneration`. On success
   * the modal flips to the proposal preview (TM4); on failure the
   * mutation's `error` is passed to TM1 via `submitError` so A13/A14
   * can render it inline.
   */
  const handleApplySubmit = async (data: SingleModeSubmitData) => {
    if (modal?.kind !== 'apply') return;
    const site = sites.find((s) => s.id === data.siteId);
    if (!site) return;

    // Snapshot the template up front — by the time the await resolves the modal
    // state may have changed (user closed mid-flight), and we want the proposal
    // paired with the template the user was actually applying. All the mapping /
    // forecast / date work happens inside the mutation (see `generateMutation`)
    // so a failure surfaces as `generateMutation.error` and shows the loading
    // state, rather than throwing here and being swallowed.
    const template = modal.template;

    try {
      const response = await generateMutation.mutateAsync({
        template,
        site,
        dateRange: data.dateRange,
        timeZone: data.timeZone,
        demand: data.forecast,
      });
      // Re-check the modal state — the user may have dismissed mid-generate.
      // Re-opening with the proposal here would feel jarring; drop it silently.
      setModal((current) =>
        current?.kind === 'apply'
          ? { kind: 'proposal', template, generationId: response.id, proposal: response.proposal }
          : current,
      );
    } catch {
      // Error is exposed via generateMutation.error → TM1's submitError (A13/A14).
    }
  };

  /**
   * TM4 commit: passes the consent checkbox state through to rotagen.
   * Closes the modal on success; the error stays on TM4 via
   * `commitMutation.error` so the consent-hint affordance can render.
   */
  const handleProposalCommit = async (options: { replaceExistingRota: boolean }) => {
    if (modal?.kind !== 'proposal') return;
    try {
      await commitMutation.mutateAsync({
        generationId: modal.generationId,
        replaceExistingRota: options.replaceExistingRota,
      });
      toast({
        title: t`Rota committed`,
        description: t`"${modal.template.name}" applied to the selected period.`,
      });
      // Surface the one-shot feedback prompt for this generation once the
      // commit has succeeded, then close the proposal modal.
      setCommittedGenerationId(modal.generationId);
      closeModal();
    } catch {
      // Error is exposed via commitMutation.error → TM4's commitError.
    }
  };

  if (templatesQuery.isError) {
    return (
      <div className="space-y-6">
        <PageHeader title={t`Rota Templates`} size={52} />
        <QueryError error={templatesQuery.error} onRetry={() => void templatesQuery.refetch()} />
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', 'rotaready:space-y-0')}>
      <TemplatesHeader
        title={t`Rota Templates`}
        search={filters.search}
        onSearchChange={(value) => setFilter('search', value)}
        onCreate={handleCreateNew}
      />

      <div className="space-y-6 rotaready:space-y-3 rotaready:p-[10px]">
        <TemplatesBanner />

        <Card className="rotaready:border-0 rotaready:bg-transparent rotaready:shadow-none">
          {/* Card padding is zeroed for the Rotaready layout so the filter bar
            sits flush with the table. Native keeps the padded card surface. */}
          <CardContent className="p-0">
            <div className="p-4 rotaready:p-[10px]">
              <TemplatesFilters filters={filters} setFilter={setFilter} sites={sites} />
            </div>
            {hasActiveFilters && (
              <div className="flex items-center justify-between border-t px-4 py-2">
                {/* Explicit singular/plural branches rather than ICU `plural()`
                  — keeps each catalogue entry a plain sentence (matches the
                  `formatLastUsed` convention) and avoids unformatted ICU. */}
                <p className="text-muted-foreground text-sm">
                  {filteredTemplates.length === 1 ? (
                    <Trans>{filteredTemplates.length} template found</Trans>
                  ) : (
                    <Trans>{filteredTemplates.length} templates found</Trans>
                  )}
                </p>
                <Button variant="ghost" size="sm" onClick={clearAll}>
                  <Trans>Clear all filters</Trans>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <TemplatesBulkActions
          count={visibleSelection.length}
          selectedStatuses={selectedStatuses}
          onBulkDelete={handleBulkDelete}
          onBulkDuplicate={handleBulkDuplicate}
          onBulkExport={handleBulkExport}
          onBulkPublish={handleBulkPublish}
          onBulkUnpublish={handleBulkUnpublish}
          onBulkRestore={handleBulkRestore}
          onClear={() => setSelection(new Set())}
        />

        {templatesQuery.isLoading || sitesQuery.isLoading || shiftTypesQuery.isLoading ? (
          <TemplatesSkeleton />
        ) : filteredTemplates.length === 0 ? (
          <TemplatesEmptyState
            onCreateNew={handleCreateNew}
            filtered={(templatesQuery.data?.length ?? 0) > 0}
            onClearFilters={clearAll}
          />
        ) : (
          <>
            <Card className="rotaready:rounded-none rotaready:border-0 rotaready:bg-transparent rotaready:p-[10px] rotaready:shadow-none">
              <TemplatesTable
                templates={pagedTemplates}
                selection={selection}
                onSelectionChange={setSelection}
                justActivatedId={justActivatedId}
                onPreview={(t) => setModal({ kind: 'preview', template: t })}
                onEdit={handleEdit}
                onDuplicate={(t) => setModal({ kind: 'duplicate', template: t })}
                onApplyToRota={(t) => setModal({ kind: 'apply', template: t })}
                onDelete={handleDelete}
                onPublish={handlePublish}
                onUnpublish={handleUnpublish}
                onRestore={handleRestore}
                onExport={handleExport}
              />
            </Card>
            {pageCount > 1 && (
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  aria-label={t`Previous page`}
                >
                  <ChevronLeft className="h-4 w-4" />
                  <Trans>Previous</Trans>
                </Button>
                <span className="text-muted-foreground text-sm">
                  <Trans>
                    Page {page} of {pageCount}
                  </Trans>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === pageCount}
                  aria-label={t`Next page`}
                >
                  <Trans>Next</Trans>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {modal?.kind === 'preview' && (
        <TemplatePreviewModal
          template={modal.template}
          onClose={closeModal}
          onGenerate={
            modal.template.status === 'deleted'
              ? undefined
              : () => setModal({ kind: 'apply', template: modal.template })
          }
        />
      )}

      {modal?.kind === 'duplicate' && (
        <DuplicateTemplateModal
          template={modal.template}
          onConfirm={handleDuplicateConfirm}
          onCancel={closeModal}
        />
      )}

      {modal?.kind === 'apply' && (
        <ApplyToRotaModal
          template={modal.template}
          sites={sites}
          onSubmit={handleApplySubmit}
          onCancel={closeModal}
          isLoading={generateMutation.isPending}
          submitError={generateMutation.error}
        />
      )}

      {modal?.kind === 'proposal' && (
        <Dialog open onOpenChange={(open) => !open && closeModal()}>
          <DialogContent
            showCloseButton={false}
            className="flex max-h-[90vh] w-[95vw] max-w-5xl flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
          >
            {/* ProposalView renders its own header/title; keep a hidden one for a11y. */}
            <DialogTitle className="sr-only">
              <Trans>Proposed rota</Trans>
            </DialogTitle>
            <DialogDescription className="sr-only">
              <Trans>Review the generated shifts, then commit them to the rota.</Trans>
            </DialogDescription>
            <ProposalView
              proposal={modal.proposal}
              onCommit={handleProposalCommit}
              onDiscard={closeModal}
              onAdjust={() => setModal({ kind: 'apply', template: modal.template })}
              isCommitting={commitMutation.isPending}
              commitError={commitMutation.error}
            />
          </DialogContent>
        </Dialog>
      )}

      {committedGenerationId && <PostCommitFeedback generationId={committedGenerationId} show />}

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete?.kind === 'bulk' ? (
                <Trans>Delete selected templates?</Trans>
              ) : (
                <Trans>Delete this template?</Trans>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.kind === 'bulk' ? (
                <Trans>This deletes the {pendingDelete.ids.length} selected templates.</Trans>
              ) : (
                <Trans>This deletes &quot;{pendingDelete?.template.name}&quot;.</Trans>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Alert variant="warning">
            <TriangleAlert />
            <AlertDescription>
              {pendingDelete?.kind === 'bulk' ? (
                <Trans>
                  Deleted templates can&apos;t be edited or used to generate a rota. You can restore
                  them at any time from the Deleted status filter.
                </Trans>
              ) : (
                <Trans>
                  Deleted templates can&apos;t be edited or used to generate a rota. You can restore
                  it at any time from the Deleted status filter.
                </Trans>
              )}
            </AlertDescription>
          </Alert>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="border border-destructive bg-transparent text-destructive hover:bg-destructive/10"
            >
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * A delete awaiting confirmation — a single template or the current bulk
 * selection. `null` when no confirmation dialog is open.
 */
type PendingDelete =
  | { kind: 'single'; template: SavedTemplate }
  | { kind: 'bulk'; ids: string[] }
  | null;

/**
 * Open-modal state for the landing route. A single discriminated
 * union keeps the state cohesive across the multi-step Apply-to-Rota
 * flow (TM1 form → TM4 proposal preview).
 */
type OpenModal =
  | { kind: 'preview'; template: SavedTemplate }
  | { kind: 'duplicate'; template: SavedTemplate }
  | { kind: 'apply'; template: SavedTemplate }
  | {
      kind: 'proposal';
      template: SavedTemplate;
      generationId: string;
      proposal: RotaProposal;
    }
  | null;
